/**
 * Insert or replace one canonical entity in every matching array-backed query.
 * The updater reads the latest cache value so concurrent mutations cannot
 * overwrite one another with an older snapshot.
 */
export function mergeEntityIntoListCaches(
    queryClient,
    { queryKey, entity, temporaryId = null, matches = () => true, sort = null }
) {
    if (!entity?.id) return;

    const entries = queryClient.getQueriesData({ queryKey });
    for (const [cachedQueryKey] of entries) {
        queryClient.setQueryData(cachedQueryKey, (current) => {
            if (!Array.isArray(current)) return current;

            const withoutPrevious = current.filter(
                (candidate) =>
                    candidate?.id !== entity.id &&
                    (temporaryId == null || candidate?.id !== temporaryId)
            );

            if (!matches(cachedQueryKey, entity)) return withoutPrevious;

            const next = [entity, ...withoutPrevious];
            return typeof sort === "function" ? next.sort(sort) : next;
        });
    }
}

// Keep opaque cursors unchanged until refetch. Do not trim the first page after
// insertion: doing so would drop a row between its old cursor and the next page.
export function mergeEntityIntoPagedListCaches(
    queryClient,
    { queryKey, entity, matches = () => true, sort }
) {
    if (!entity?.id) return;
    for (const [key] of queryClient.getQueriesData({ queryKey })) {
        queryClient.setQueryData(key, (current) => {
            if (!current?.pages?.length || !matches(key, entity)) return current;
            const pages = current.pages.map((page) => ({
                ...page,
                data: (page.data || []).filter((row) => row.id !== entity.id)
            }));
            pages[0].data = [entity, ...pages[0].data];
            if (sort) pages[0].data.sort(sort);
            return { ...current, pages };
        });
    }
}
